// ---------------------------------------------------------------------------
// The outlet: the current route's page, mounted per page, in its frame (R12c, R12d)
//
// What @adornis/router's outlet and sdr-router.ts's outletWrapper did, minus
// their bugs, and without importing a page: sdr-router.ts hands in the render
// map (route name → page) and the frame, the DOM suite hands in stubs.
//
//   - A blank campaign id shows the outlet's own view: the heading "Invalid
//     campaign ID" and a callout with a link back to the Overview, built from
//     the ports (PageHeader, Callout, Button), so a keyboard reaches the way
//     back and the page change finds an h1. Params arrive decoded (B14), so
//     /campaign/%20/edit reaches it; the router takes the link's click.
//   - The page is wrapped in remount(pageKey(state), …): a change of name or
//     params mounts it again (B6, router/remount.ts).
//   - Routes with frame 'none' (optOut, admin, adminLogin, and since R12d
//     connected, d2) bring their own layout; the others go into the console
//     frame (frame.ts), also the dashboard an unknown path falls back to (B1).
//   - Every old page renders inside .ndi-legacy, the legacy island (R12b2,
//     tailwind.css): in the frame its box is the island, without the frame a
//     wrapper of its own. Old pages keep 16px / 1.5 under the design system's
//     14px / 22px until each one is rebuilt. The outlet's own view is the
//     system's and stays out of it. R13 rebuilt the opt-out page, the admin
//     sign-in and /connected on the system's AuthLayout: without the frame
//     they sit on its base, with no wrapper. R15b rebuilt the Overview
//     (dashboard) from the system's ConsoleOverview: in the frame, its box is
//     no island, also where an unknown path falls back to it. R15d rebuilt
//     its campaign list on the system's DataTable, the last part of it that
//     kept an island of its own: the Overview has none now.
//
// The result is always a template, so the remount has a part of its own and
// never shares the guard's (app-root.ts).
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { button } from '../design/button.js';
import { callout } from '../design/callout.js';
import { pageHeader } from '../design/page-header.js';
import { pathFor } from '../router/match.js';
import { remount } from '../router/remount.js';
import { pageKey, type RouteState } from '../router/router.js';
import { ROUTES, type RouteName } from '../router/routes.js';
import type { AppFrame } from './frame.js';

export type PageRender = (params: Readonly<Record<string, string>>) => unknown;
export type PageMap = Readonly<Record<RouteName, PageRender>>;

export interface OutletInput {
  state: RouteState;
  pages: PageMap;
  frame: AppFrame;
}

const invalidCampaignId = () =>
  html`<div class="flex flex-col gap-6 p-8">
    ${pageHeader({ title: 'Invalid campaign ID' })}
    ${callout(
      { tone: 'critical', actions: button({ size: 'sm', href: pathFor('dashboard') ?? '/' }, 'Back to Overview') },
      'This address names no campaign. Open the campaign from the Overview.',
    )}
  </div>`;

/** What page() returns for a campaign route with a blank id. */
const BLANK_ID = Symbol('blank campaign id');

/** The page for the state, or BLANK_ID. */
function page(state: RouteState, pages: PageMap): unknown {
  const render = pages[state.name as RouteName];
  if ('campaignId' in state.params) {
    const campaignId = state.params.campaignId?.trim();
    if (!campaignId) return BLANK_ID;
    return render({ ...state.params, campaignId });
  }
  return render(state.params);
}

/** Rebuilt on the design system: no legacy island, without the frame (R13) or in it (R15b). */
const OWN_BASE: ReadonlySet<string> = new Set<RouteName>(['optOut', 'adminLogin', 'connected', 'dashboard']);

export function appOutlet({ state, pages, frame }: OutletInput): TemplateResult {
  const shown = page(state, pages);
  const blank = shown === BLANK_ID;
  // The outlet's own view is the system's, and so is a rebuilt page: only an old page's box is the island.
  const island = !blank && !OWN_BASE.has(state.name);
  const content = remount(pageKey(state), blank ? invalidCampaignId() : shown);
  const route = ROUTES[state.name as RouteName];
  if (route?.frame === 'none')
    return OWN_BASE.has(state.name) ? html`${content}` : html`<div class="ndi-legacy">${content}</div>`;
  return frame.render({ route: state.name, content, island });
}
